import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TabsetComponent } from 'ngx-bootstrap/tabs';
import { from, Observable, Subject } from 'rxjs';
import { DetailFormDataModel } from 'src/app/config/model/detail-form-data.model';
import { FormModel } from 'src/app/shared/model/form.model';
import { ConfigService } from '../../config/config.service';
import {EsriExpand, EsriReactiveUtils, EsriRequest} from 'src/app/config/esri.module';
import { MessageService } from '../../core/service/message.service';
import { UserService } from '../../core/service/user.service';
import { ConfirmationService } from '../../shared/component/confirmation-modal/shared/confirmation.service';
import { ArbeitsbereichService } from '../../shared/service/arbeitsbereich.service';
import { DetailViewFormBuilderService } from '../../shared/service/detail-view-form-builder.service';
import { DomUtilService } from '../../shared/service/dom-util.service';
import { LayerContainerService } from '../../shared/service/layer-container.service';
import { LayerInfosService } from '../../shared/service/layer-infos.service';
import { MainMapService } from '../../shared/service/main-map.service';
import { SketchGeometryService } from '../../shared/service/sketch-geometry.service';
import { WidgetNotifyService } from '../../shared/service/widget-notify.service';
import { DetailViewFeatureParams } from './detail-view.model';
import { DetailViewNotifyService } from './shared/detailview-notify.service';
import { DetailViewCrudService } from './shared/detail-view-crud.service';
import { Arbeitsbereich } from '../../config/model/arbeitsbereich-config.model';
import { NotificationType } from 'angular2-notifications';
import { AttributesService } from '../../shared/service/attributes.service';
import { CONSTANTS_VALUE } from '../../config/constants.module';
import { AgGridNotifyService } from '../../bottom-panel/services/ag-grid-notify.service';
import { SelectionNotifyService } from '../../shared/service/selection-notify.service';
import { Workspace } from '../../shared/enum/workspace.enum';
import { BaseWidget } from '../tools/base-widget/base-widget';
import { catchError, takeUntil } from 'rxjs/operators';
import { UrlStateHelperService } from '../../shared/service/url-state/url-state-helper.service';
import {SelectionService} from "../../shared/service/selection.service";

@Component({
    selector: 'detail-view',
    templateUrl: './detail-view.component.html',
    styleUrls: ['./detail-view.component.scss'],
    standalone: false
})
export class DetailViewComponent extends BaseWidget implements OnInit, OnDestroy {
  @ViewChild('detailViewComponentRef', { static: true }) detailViewComponentRef: ElementRef;
  @ViewChild('tabsetRef', { static: true }) tabsetRef: TabsetComponent;
  @ViewChild('detailViewFormRef', { static: false }) detailViewFormRef: ElementRef;
  @ViewChild('dragArea', { static: true }) dragArea: ElementRef;

  @Input() featureParams: DetailViewFeatureParams;

  private destroy$ = new Subject<void>();

  public isLocked: boolean;
  public disabledSend: boolean;
  public graphicFormGroup: UntypedFormGroup;
  public viewExpanded = false;
  public smallScreen = false;
  public featureIsEditable: boolean;
  public showPartFeatures = false;
  public attributesTabActive = true;
  public detailFormDataModel: DetailFormDataModel;
  public formattedFormFields: FormModel;
  // user role based actions
  public allowEditFeature = false;
  public allowDeleteFeature = false;
  public activatedEditTool: boolean;
  public featureGeometryEdited = false;
  public isDenkmalpflege = false;

  constructor(
    public mainMapService: MainMapService,
    public widgetNotifyService: WidgetNotifyService,
    private _arbeitsbereichService: ArbeitsbereichService,
    private messageService: MessageService,
    private changeDetectorRef: ChangeDetectorRef,
    private detailViewFormBuilderService: DetailViewFormBuilderService,
    private domUtilService: DomUtilService,
    private detailViewNotifyService: DetailViewNotifyService,
    private layerInfosService: LayerInfosService,
    private userService: UserService,
    private configService: ConfigService,
    private sketchGeometryService: SketchGeometryService,
    private layerContainerService: LayerContainerService,
    private detailViewCrudService: DetailViewCrudService,
    private confirmationService: ConfirmationService,
    private attributesService: AttributesService,
    private agGridNotifyService: AgGridNotifyService,
    private selectionNotifyService: SelectionNotifyService,
    private readonly urlStateHelperService: UrlStateHelperService,
    private readonly selectionService: SelectionService
  ) {
    super(mainMapService, widgetNotifyService);
    this.isDenkmalpflege = this.checkIsDenkmalpflege();
  }

  ngOnInit() {
    this.isLocked = true;
    this.disabledSend = true;
    this.initializeWidget();
    this.initializeFormWithFeatureParams(this.featureParams);
    this.setupSubscription();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkIsDenkmalpflege(): boolean {
    const selectedArbeitsbereich: Arbeitsbereich = this._arbeitsbereichService.selectedArbeitsbereich;
    return Workspace.denkmalpflege === selectedArbeitsbereich.id;
  }

  private initializeWidget(): void {
    const mapView: __esri.MapView = this.mainMapService.mapView;
    // create an expand instance
    const expandProperties: __esri.ExpandProperties = {
      view: mapView,
      id: 'DetailViewComponent',
      content: this.detailViewComponentRef.nativeElement,
      expandIcon: 'table',
      collapseIcon: 'chevrons-left',
    };
    // create a new expand widget 'button' for the list-view
    this.expandWidget = new EsriExpand(expandProperties);
    // add widget to the main map
    mapView.ui.add(this.expandWidget, 'top-left');
    this.mainMapService.registerWidget(this.expandWidget);

    // Open this widget, will close other widgets
    EsriReactiveUtils.watch(
      () => this.expandWidget?.expanded,
      () => {
        if (this.expandWidget.expanded) {
          this.mainMapService.closeOtherWidgets(this.expandWidget);
        }
      }
    );
  }

  private setupSubscription(): void {
    this.detailViewNotifyService.onDetailViewExpandSubject.pipe(takeUntil(this.destroy$)).subscribe(expand => {
      this.viewExpanded = expand;
    });

    this.userService.onUserUpdateSubject.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setUserRoles(this.featureParams.featureLayerUrl);
    });

    this.sketchGeometryService.onUpdatedGeometryCompleteSubject.pipe(takeUntil(this.destroy$)).subscribe(isEditing => {
      this.featureGeometryEdited = isEditing;
    });

    this.sketchGeometryService.onUpdatedGeometryCompleteSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe(completeEditing => {
        if (completeEditing && this.sketchGeometryService.sketchStateCompleted) {
          this.detailFormDataModel.selectedFeature.geometry = this.sketchGeometryService.newFeatureGeometry;
        }
        this.updateKmVonKmBis(
          this.detailFormDataModel.selectedFeature,
          this.detailFormDataModel.selectedFeature.attributes.LINIE
        );
      });

    this.detailViewNotifyService.onChangeVerkaufsObjectLinieNrSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe(lineNr => {
        this.updateNewFeatureData();
        this.updateKmVonKmBis(this.detailFormDataModel.selectedFeature, lineNr);
      });

    // EGRID
    this.detailViewNotifyService.onSetTransObjektArtSubject.pipe(takeUntil(this.destroy$)).subscribe(transObjektArt => {
      this.updateNewFeatureData();
      this.updateEGRID(this.detailFormDataModel.selectedFeature, transObjektArt);
    });

    this.widgetNotifyService.onMapClickedSubject.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.activatedEditTool = false;
      this.detailViewCrudService.deactivateEditTool();
    });
  }

  private updateNewFeatureData() {
    if (this.sketchGeometryService.newFeatureGeometry) {
      this.detailFormDataModel.selectedFeature.geometry = this.sketchGeometryService.newFeatureGeometry;
    }
    this.patchValues(this.graphicFormGroup, this.detailFormDataModel);
  }

  /**
   * @function initializeFormWithFeatureParams
   * @param {DetailViewFeatureParams} featureParams
   * @description
   */
  private initializeFormWithFeatureParams(featureParams: DetailViewFeatureParams) {
    this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.wait);
    this.featureParams = featureParams;
    // set feature layer URL (unique)
    this.setFeatureLayerUrl();
    this.setUserRoles(this.featureParams.featureLayerUrl);

    this.layerInfosService.getLayerInfos(this.featureParams.featureLayerUrl).subscribe(layerInfos => {
      this.featureParams.layerInfos = layerInfos.data;
      this.setRelatedTableConfigs();
      this.buildAttributeEditorForm();
      this.changeDetectorRef.detectChanges();
      this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
      this.expandWidget.expand();
    });
  }

  private updateEGRID(feature: any, transObjektArt: string) {
    this.attributesService.getEGRIDFromAV(feature.geometry, transObjektArt).subscribe(egrid => {
      this.patchValuesAfterUpdateGeometry(egrid, this.detailFormDataModel, this.graphicFormGroup);
    });
  }

  /**
   * @function updateKmVonKmBis
   * @description update Km(Anf.) und Km(Ende) wenn die Geometry des Features geändert ist
   */
  private updateKmVonKmBis(feature: any, lineNummber?: number) {
    this.attributesService.updateFeatureAttributes(feature, lineNummber).subscribe(featureAttr => {
      if (!featureAttr) {
        return;
      }
      this.patchValuesAfterUpdateGeometry(featureAttr, this.detailFormDataModel, this.graphicFormGroup);
    });
  }

  /**
   * @function buildAttributeEditorForm
   * @description Erstellet die Detailview Form anhand der attribute von geklickten Feature aus IdentifyService
   */
  private buildAttributeEditorForm() {
    this.detailFormDataModel = this._arbeitsbereichService.setDetailFormDataModel(this.featureParams);
    // If formModel is not valid, do not show detail-view
    if (!this.detailFormDataModel) {
      this.messageService.error('NOTIFICATIONS.ERROR.OPEN_DETAILVIEW_INVALID');
      this.close();
      return;
    }
    // has form part features
    if (this.detailFormDataModel?.partFeaturesTableConfig?.columns.length > 0) {
      this.showPartFeatures = true;
    }
    this.featureIsEditable = this._arbeitsbereichService.detailFormDataModel.featureIsEditable;
    this.activatedEditTool = false;

    this.formattedFormFields = this.detailViewFormBuilderService.buildFormJson(
      this.detailFormDataModel.selectedFeatureFields,
      this.detailFormDataModel.configFormFields
    );

    if (this.formattedFormFields) {
      this.graphicFormGroup = this.createGraphicFormGroup();
    } else {
      this.messageService.error('NOTIFICATIONS.ERROR.OPEN_DETAILVIEW_INVALID');
      this.close();
    }
  }

  /**
   * @function checkStatusSapId
   * @param sapId
   * @description check if sap id on sap is exist
   */
  private checkStatusSapId(sapId: number): Observable<any> {
    // service check sap id
    const checkStatusUrl = this.configService.baseUrl + this.configService.appConfig.checkStatusSapId;
    // check sap id
    return from(
      EsriRequest(checkStatusUrl, {
        query: {
          f: 'json',
          VerkaufsobjektSapId: sapId,
        },
        responseType: 'json',
      })
    ).pipe(
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * @function sendVerkaufsObjekt
   * @param sapId
   * @description send sap id to SAP and return true if success
   */
  private sendVerkaufsObjekt(sapId: number): Observable<any> {
    // service check sap id
    const sendVerkaufsObjektUrl = this.configService.baseUrl + this.configService.appConfig.sendVerkaufsObjekt;
    // check sap id

    return from(
      EsriRequest(sendVerkaufsObjektUrl, {
        query: {
          f: 'json',
          VerkaufsobjektSapId: sapId,
        },
        responseType: 'json',
      })
    ).pipe(
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   *@description initial neues FormGroup für geklicktes Graphic
   */
  private createGraphicFormGroup() {
    const graphicFormGroup = new UntypedFormGroup({
      initial: new UntypedFormControl('initial'),
    });
    graphicFormGroup.markAsTouched();
    return graphicFormGroup;
  }

  /**
   *@description check if form is valid
   *@return true === valid, false != valid
   */
  private isFormValid(): boolean {
    // if Form is invalid, do not save and throw error message
    if (!this.graphicFormGroup.valid) {
      this.domUtilService.setFormInvalidBootstrapCSSFlag(this.detailViewFormRef);
      this.messageService.error('DETAIL_VIEW.FORM_VALIDATION.INVALID_FORM');
      // show error-message on every field
      this.detailViewNotifyService.validateFeatureSubject.next();
      return false;
    }
    return true;
  }

  /**
   * @function removeGraphicsLayer
   * @description Remove existing marker layers on the main map
   */
  private removeGraphicsLayer() {
    // holt alle Markers von der Map-View
    const foundMarkerLayers = this.mainMapService.mapView.map.allLayers.find(function (layer) {
      return layer.id === '_markerGraphicsLyr';
    });

    // Entfernt alle Markers von der Map-View
    if (foundMarkerLayers) {
      this.mainMapService.mapView.map.remove(foundMarkerLayers);
    }
  }

  /**
   * @function setFeatureLayerUrl
   * @description set feature layer url to current params
   */
  private setFeatureLayerUrl() {
    if (this._arbeitsbereichService.selectedArbeitsbereich.editableLayers.length === 0) return;

    if (this.featureParams.workspace === this._arbeitsbereichService.selectedArbeitsbereich.id) {
      const formConfig = this._arbeitsbereichService.getFormConfig(
        this._arbeitsbereichService.selectedArbeitsbereich,
        this.featureParams.layerId
      );
      if (!formConfig) return;
      if (formConfig.editableLayerId && formConfig.editableLayerId !== '') {
        const editableLayer = this.layerContainerService.layerList.filter(
          f => f.esriLayer.id === formConfig.editableLayerId
        )[0];
        this.featureParams.featureLayerUrl = editableLayer.esriLayer['parsedUrl'].path;
      }
    }
  }

  /**
   * @function setRelatedTableConfigs
   * @description sets the related-table configuration
   */
  private setRelatedTableConfigs() {
    const fkArbeitsbereich: string = this._arbeitsbereichService.selectedArbeitsbereich.id;
    const fkIdentifyLayerId: number = this.featureParams.layerId;
    const relationshipInfos = this.configService.relationshipTablesConfig.relationshipInfos.filter(
      f => f.fkArbeitsbereich === fkArbeitsbereich && f.fkIdentifyLayerId === fkIdentifyLayerId
    );
    if (relationshipInfos.length > 1) {
      throw new Error('Multiple RelationshipTable configured for Arbeitsbereich ' + fkArbeitsbereich);
    }
    this.featureParams.relatedTableConfigs =
      !this.featureParams.createMode && relationshipInfos.length === 1 ? relationshipInfos[0].tables : [];
  }

  /**
   * @name setUserRoles
   * @description set user rols on featurelayer by actions
   */
  private setUserRoles(featureLayerUrl: string) {
    const fkLayer = this.layerContainerService.fkLayerFromUrl(featureLayerUrl);
    if (!fkLayer) {
      this.allowEditFeature = false;
      this.allowDeleteFeature = false;
    } else {
      this.allowEditFeature = this.userService.editFeaturePermitted(fkLayer);
      this.allowDeleteFeature = this.userService.deleteFeaturePermitted(fkLayer);
    }
  }

  /**
   * @function patchValues
   * @param {FormGroup} graphicFormGroup
   * @param {DetailFormDataModel} detailFormDataModel
   * @description maps the changes in the graphicFormGroup to the selectedFeature in the detailFormDataModel
   */
  private patchValues(graphicFormGroup: FormGroup, detailFormDataModel: DetailFormDataModel) {
    if (!detailFormDataModel.selectedFeature) {
      return;
    }
    Object.assign(detailFormDataModel.selectedFeature.attributes, graphicFormGroup.value);
  }

  private patchValuesAfterUpdateGeometry(
    updatedFeatureAttr: any,
    detailFormDataModel: DetailFormDataModel,
    graphicFormGroup: FormGroup
  ) {
    if (!detailFormDataModel.selectedFeature) {
      return;
    }
    Object.assign(graphicFormGroup.value, updatedFeatureAttr);
    Object.assign(detailFormDataModel.selectedFeature.attributes, updatedFeatureAttr);
    this.detailViewNotifyService.onUpdatePatchGraphicFormValuesSubject.next();
  }

  /* Public methods
  ========================== */

  public setFeatureParams(featureParams: DetailViewFeatureParams) {
    return (this.featureParams = featureParams);
  }


  /**
   * @function cancel
   * @description Cancels creation on detail view panel
   */
  public cancel() {
    this.close();
    this.detailViewNotifyService.featureCreationCanceledSubject.next();
  }

  /**
   *@function onSave
   *@description Save Detail View Form to FeatureLayer
   */
  public onSave() {
    if (!this.isFormValid()) return;
    this.patchValues(this.graphicFormGroup, this.detailFormDataModel);
    if (!this.sketchGeometryService.sketchStateCompleted && this.featureGeometryEdited) {
      this.detailFormDataModel.selectedFeature.geometry = this.sketchGeometryService.newFeatureGeometry;

      this.attributesService
        .updateFeatureAttributes(
          this.detailFormDataModel.selectedFeature,
          this.detailFormDataModel.selectedFeature.attributes.LINIE
        )
        .subscribe(featureAttr => {
          if (!featureAttr) {
            return;
          }
          this.patchValuesAfterUpdateGeometry(featureAttr, this.detailFormDataModel, this.graphicFormGroup);

          this.detailViewCrudService
            .saveFeature(this.detailFormDataModel, this.featureParams, this.featureGeometryEdited)
            .subscribe(() => {
              this.disabledSend = false;
              this.activatedEditTool = false;
              this.sketchGeometryService.sketchStateCompleted = false;
            });
        });
    } else {
      this.detailViewCrudService
        .saveFeature(this.detailFormDataModel, this.featureParams, this.featureGeometryEdited)
        .subscribe(() => {
          this.disabledSend = false;
          this.activatedEditTool = false;
          this.sketchGeometryService.sketchStateCompleted = false;
          this.agGridNotifyService.refreshData();
        });
    }
  }

  /**
   * @function onDelete
   * @description shows the confirmation window to confirm the delete
   * and execute it in yes case
   */
  public onDelete() {
    this.confirmationService
      .showConfirmationModal('MODAL.CONFIRM.DELETE_VERKAUFSOBJEKT')
      .subscribe(deleteFeatureAllowed => {
        if (!deleteFeatureAllowed) return;
        this.detailViewCrudService.deleteFeature().subscribe(() => {
          this.close();
        });
      });
  }

  /**
   * @name activateEditTool
   * @description Activates the edit tool if not activated.
   */
  public activateEditTool() {
    if (this.activatedEditTool) return;
    this.activatedEditTool = true;
    this.mainMapService.deactivateMapInteraction();
    this.detailViewCrudService.startEditingGeometry(this.featureParams);
  }

  /**
   * @function close
   * @description Close detail view panel
   */
  public close() {
    this.activatedEditTool = false;
    this.detailViewCrudService.deactivateEditTool();
    this.featureGeometryEdited = false;
    this.selectionService.removeAllSelectedGraphics();
    this.removeGraphicsLayer();
    this.destroySelf();
    this.selectionNotifyService.clearSelection();
    this.urlStateHelperService.navigateToWorkspace(this._arbeitsbereichService.selectedArbeitsbereich);
  }

  /**
   * @function sendSapId
   * @description send sap id to SAP
   */
  public sendSapId() {
    if (!this.graphicFormGroup.controls.ID_SAP.value) {
      return;
    }
    const sapId: number = parseInt(this.graphicFormGroup.controls.ID_SAP.value);
    this.sendVerkaufsObjekt(sapId).subscribe(resp => {
      if (resp.data.status === 'SUCCESS' || resp.data.status === 'Accepted' || resp.data.status === "") {
        this.messageService.success('NOTIFICATIONS.SUCCESS.SEND_OK', 10000);
      } else {
        this.messageService.createCustomMessage(
          `Status Senden an SAP: ${resp.data.status}`,
          resp.data.message,
          NotificationType.Warn,
          {
            timeOut: 10000,
          }
        );
      }
    });
  }

  /**
   * @function checkSapId
   * @description Check sap id and set value on isLocked
   */
  public checkSapId() {
    if (!this.graphicFormGroup.controls.ID_SAP.value) {
      return;
    }
    const sapId: number = parseInt(this.graphicFormGroup.controls.ID_SAP.value);
    this.checkStatusSapId(sapId).subscribe(resp => {
      this.isLocked = resp.data.isLocked;
      if (resp.data.isLocked) {
        this.messageService.warning('NOTIFICATIONS.INFO.CHECK_SAP_ID', 10000);
      } else {
        // send notification
        this.messageService.success('NOTIFICATIONS.SUCCESS.CHECK_SAP_ID', 10000);
        this.detailViewNotifyService.onCheckSAPIdSuccessSubject.next(false);
      }
    });
  }
}
